// FILE: src/pages/StudentDashboardPage.jsx
// The course homepage (plan WP-8 task 3). Every time figure on this page is
// the server's (GET /api/progress/summary, time engine WP-3); the page
// formats, it never counts. Shows: what to do next, today's time against the
// 5-hour cap (MTO 17.1), the break state (17.2), core and homelink hours
// (CD3.1), the enrolment days left (6.3), every lesson's status, and the
// three support channels with contacts and hours (10.1, 12.2).
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CoffeeOutlinedIcon from "@mui/icons-material/CoffeeOutlined";
import { fetchProgress, fetchProgressSummary } from "../features/progressSlice";
import { fetchStudentResources } from "../features/resourceSlice";
import { selectBreakDeadline, selectLessonsBlocked } from "../features/timeSlice";
import { formatClock, formatCountdown, formatDuration } from "../utils/timeFormat";
import { ErrorState, LoadingState, PageHeader, Section, StatCard, clampPct, EmptyState } from "../components/ui";
import LessonList, { BLOCKED_REASON } from "../components/student/LessonList";
import SupportChannelsCard from "../components/student/SupportChannelsCard";
import { actionLabel, lessonPercent, mergeLessons, nextLesson } from "../components/student/lessons";
import useCurriculum, { curriculumLine } from "../hooks/useCurriculum";

const CORE_HOURS = 20; // CD3.1: 20 hours of in-class (digital) instruction
const FLEX_HOURS = 10; // flexible (homelink) hours

// Kept for other pages that format minutes.
export const formatUsage = (minutes) => {
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hrs = Math.floor(minutes / 60);
  return `${hrs}h ${(minutes % 60).toFixed(1)}min`;
};

function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function NextUpCard({ lesson, kind, blocked, allDone }) {
  const navigate = useNavigate();
  if (allDone) {
    return (
      <Card sx={{ height: "100%", bgcolor: "zun.primaryTint", borderColor: "transparent" }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
            <FlagOutlinedIcon aria-hidden sx={{ color: "primary.main" }} />
            <Typography component="h2" variant="h3">
              You've finished every lesson
            </Typography>
          </Stack>
          <Typography sx={{ mb: 2 }}>
            Well done. One last step: the course survey. Ontario's rules make it part of finishing the course.
          </Typography>
          <Button component={RouterLink} to="/student/survey" variant="contained">
            Take the course survey
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!lesson) return null;
  const pct = lessonPercent(lesson);
  const label = actionLabel(lesson.status);
  return (
    <Card
      sx={{
        height: "100%",
        color: "#FFFFFF",
        borderColor: "transparent",
        bgcolor: "#1E5B3A",
        backgroundImage: "linear-gradient(135deg, #1E5B3A 0%, #174A2F 100%)",
        position: "relative",
        overflow: "hidden"
      }}
      data-testid="next-up"
    >
      <Box
        component="svg"
        viewBox="0 0 200 200"
        aria-hidden="true"
        focusable="false"
        sx={{ position: "absolute", right: -40, top: -50, width: 200, opacity: 0.1, display: { xs: "none", sm: "block" } }}
      >
        <path d="M10 190 C 70 120, 60 80, 120 60 S 190 20, 190 0" fill="none" stroke="#fff" strokeWidth="34" strokeLinecap="round" />
      </Box>
      <CardContent sx={{ position: "relative", p: { xs: 3, md: 4 } }}>
        <Typography sx={{ fontWeight: 600, opacity: 0.92, mb: 0.5 }}>
          {lesson.status === "in-progress" ? "Continue where you left off" : "Up next"}
        </Typography>
        <Typography component="h2" sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" }, fontWeight: 700, lineHeight: 1.25, mb: 2 }}>
          {lesson.title || (kind === "homelink" ? "Homelink" : "Chapter")}
        </Typography>
        <Box sx={{ maxWidth: 420, mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            aria-label="Progress in this lesson"
            aria-valuetext={`${lesson.completedSlides} of ${lesson.totalSlides} slides`}
            sx={{ bgcolor: "rgba(255,255,255,0.25)", "& .MuiLinearProgress-bar": { bgcolor: "#FFFFFF" } }}
          />
          <Typography sx={{ mt: 1, opacity: 0.92 }}>
            {lesson.completedSlides} of {lesson.totalSlides} slides done
          </Typography>
        </Box>
        <Button
          size="large"
          disabled={Boolean(blocked)}
          aria-describedby={blocked ? "lessons-blocked-reason" : undefined}
          onClick={() => navigate(`/student/resources/${lesson._id}?resourceType=${kind}`)}
          sx={{
            bgcolor: "#FFFFFF",
            color: "#1E5B3A",
            "&:hover": { bgcolor: "#E7F1EB" },
            "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.5)", color: "#174A2F" }
          }}
        >
          {label}
        </Button>
      </CardContent>
    </Card>
  );
}

function TodayCard({ usedSec, capSec, remainingSec, untimed, continuousSec, breakAfterSec }) {
  const pct = clampPct((usedSec / capSec) * 100);
  const toBreak = breakAfterSec && continuousSec != null ? Math.max(0, breakAfterSec - continuousSec) : null;
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
          <ScheduleOutlinedIcon aria-hidden sx={{ color: "primary.main" }} />
          <Typography component="h2" variant="h3">
            Today
          </Typography>
        </Stack>
        <Typography component="p" sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.2 }} data-testid="today-usage">
          {formatDuration(usedSec)} <Box component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: "text.secondary" }}>of 5 hours</Box>
        </Typography>
        <LinearProgress variant="determinate" value={pct} aria-label="Today's lesson time" sx={{ mt: 1.5 }} />
        {untimed ? (
          <Typography sx={{ color: "text.secondary", mt: 1 }}>This account isn't timed.</Typography>
        ) : (
          <>
            {remainingSec != null && (
              <Typography sx={{ mt: 1 }}>
                <strong>{formatDuration(remainingSec)}</strong> left today
              </Typography>
            )}
            {toBreak != null && toBreak > 0 && (
              <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
                Next break due after {formatDuration(toBreak)} more learning
              </Typography>
            )}
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>Resets at midnight, Ontario time</Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function StudentDashboardPage() {
  const dispatch = useDispatch();
  const { chaptersViewed, homelinksViewed, summary, loading, error } = useSelector((state) => state.progress);
  const time = useSelector((state) => state.time);
  const breakDeadline = useSelector(selectBreakDeadline);
  const blocked = useSelector(selectLessonsBlocked);
  const { homelinks, chapters, loading: resourcesLoading, error: resourcesError } = useSelector((state) => state.resources);
  const { profile } = useSelector((state) => state.profile);
  const firstName = useSelector((state) => state.auth.user?.firstName);
  const curriculum = useCurriculum();
  const [now, setNow] = useState(Date.now());

  const load = React.useCallback(() => {
    dispatch(fetchProgress());
    dispatch(fetchProgressSummary());
    dispatch(fetchStudentResources("CHAPTER"));
    dispatch(fetchStudentResources("HOMELINK"));
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!breakDeadline) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [breakDeadline]);

  const chapterRows = useMemo(() => mergeLessons(chapters, chaptersViewed, "chapterId"), [chapters, chaptersViewed]);
  const homelinkRows = useMemo(() => mergeLessons(homelinks, homelinksViewed, "homelinkId"), [homelinks, homelinksViewed]);

  const untimed = !!(time.untimed || profile?.untimed);
  const firstLoad = (loading || resourcesLoading) && !chapters?.length && !summary;
  if (firstLoad) return <LoadingState variant="page" label="Loading your dashboard…" />;
  if ((error || resourcesError) && !chapters?.length) {
    return (
      <>
        <PageHeader title="Student Dashboard" />
        <ErrorState error={error || resourcesError} title="We couldn't load your course" onRetry={load} />
      </>
    );
  }

  const capSec = summary?.dailyCapSec || 300 * 60;
  const usedSec = time.dailyCreditedSec ?? summary?.dailyCreditedSec ?? 0;
  const remainingSec = time.dailyRemainingSec ?? summary?.dailyRemainingSec;
  const chapterSec = summary?.totals?.chapterSec || 0;
  const homelinkSec = summary?.totals?.homelinkSec || 0;
  const continuousSec = time.continuousSec ?? summary?.continuousSec;
  const daysLeft = time.daysLeft ?? summary?.daysLeft;

  const nextChapter = nextLesson(chapterRows);
  const nextHomelink = nextLesson(homelinkRows);
  const next = nextChapter || nextHomelink;
  const nextKind = nextChapter ? "chapter" : "homelink";
  const allRows = [...chapterRows, ...homelinkRows];
  const allDone = allRows.length > 0 && allRows.every((r) => r.status === "completed");
  const totalSlides = allRows.reduce((t, r) => t + r.totalSlides, 0);
  const doneSlides = allRows.reduce((t, r) => t + Math.min(r.completedSlides, r.totalSlides), 0);
  const coursePct = totalSlides ? (doneSlides / totalSlides) * 100 : 0;

  return (
    <Box>
      <PageHeader
        eyebrow={firstName ? `${greeting()}, ${firstName}` : greeting()}
        title="Student Dashboard"
        description="Your Beginner Driver Education course: what's next, your time today and how far you've come."
      >
        {curriculum && (
          <Typography sx={{ color: "text.secondary", mt: 1 }} data-testid="curriculum-version">
            Curriculum: {curriculumLine(curriculum)}
          </Typography>
        )}
      </PageHeader>

      {time.capped && !untimed && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="cap-alert">
          <AlertTitle>Daily limit reached</AlertTitle>
          You've reached today's 5-hour limit of online lessons. Lessons open again tomorrow, after midnight Ontario
          time.
        </Alert>
      )}
      {time.breakRequired && !untimed && (
        <Alert severity="warning" icon={<CoffeeOutlinedIcon />} sx={{ mb: 2 }} data-testid="break-alert">
          <AlertTitle>Break time</AlertTitle>
          You can continue in {formatCountdown(Math.max(0, breakDeadline - now))}
          {time.breakEndsAt ? ` (at ${formatClock(time.breakEndsAt)})` : ""}.
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1fr)" }, mb: 2 }}>
        {next || allDone ? (
          <NextUpCard lesson={next} kind={nextKind} blocked={blocked} allDone={allDone} />
        ) : (
          <EmptyState title="No lessons yet" description="Your lessons will appear here as soon as they're published." />
        )}
        <TodayCard
          usedSec={usedSec}
          capSec={capSec}
          remainingSec={remainingSec}
          untimed={untimed}
          continuousSec={continuousSec}
          breakAfterSec={summary?.breakAfterSec}
        />
      </Box>

      {blocked && (
        <Typography id="lessons-blocked-reason" sx={{ mb: 2, color: "text.secondary" }} data-testid="lessons-blocked">
          {BLOCKED_REASON[blocked]}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          mb: 4
        }}
      >
        <StatCard
          icon={MenuBookOutlinedIcon}
          label="Chapter hours"
          value={formatDuration(chapterSec)}
          progress={(chapterSec / (CORE_HOURS * 3600)) * 100}
          progressLabel="Chapter hours toward 20"
          note={`of ${CORE_HOURS} hours of in-class lessons`}
        />
        <StatCard
          icon={HomeWorkOutlinedIcon}
          label="Homelink hours"
          value={formatDuration(homelinkSec)}
          progress={(homelinkSec / (FLEX_HOURS * 3600)) * 100}
          progressLabel="Homelink hours toward 10"
          note={`of ${FLEX_HOURS} flexible hours`}
        />
        <StatCard
          icon={FlagOutlinedIcon}
          label="Course progress"
          value={`${Math.round(coursePct)}%`}
          progress={coursePct}
          progressLabel="Course progress"
          note={`${doneSlides} of ${totalSlides} slides`}
        />
        <StatCard
          icon={EventOutlinedIcon}
          label="Enrolment"
          value={daysLeft == null ? "—" : `${daysLeft} ${daysLeft === 1 ? "day" : "days"}`}
          tone={daysLeft != null && daysLeft <= 30 ? "warning" : "primary"}
          note={
            summary?.enrolmentExpiresAt
              ? `left, until ${new Date(summary.enrolmentExpiresAt).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}`
              : "left to finish the course"
          }
        />
      </Box>

      <Stack spacing={2}>
        <Section
          id="chapters"
          title="Chapters"
          description="In-class lessons. Each one opens when you finish the one before it."
        >
          {chapterRows.length ? (
            <LessonList rows={chapterRows} kind="chapter" blocked={blocked} untimed={untimed} blockedReasonId="lessons-blocked-reason" />
          ) : (
            <EmptyState compact title="No chapters yet" description="Chapters appear here when they're published." />
          )}
        </Section>
        <Section id="homelinks" title="Homelinks" description="Flexible-hour activities that build on the chapters.">
          {homelinkRows.length ? (
            <LessonList rows={homelinkRows} kind="homelink" blocked={blocked} untimed={untimed} blockedReasonId="lessons-blocked-reason" />
          ) : (
            <EmptyState compact title="No homelinks yet" description="Homelinks appear here when they're published." />
          )}
        </Section>
        <SupportChannelsCard />
      </Stack>
    </Box>
  );
}
